import React, { forwardRef, useState } from 'react';
import { css, cx } from '../../../customEmotion';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { withStyles } from '@material-ui/core/styles';

import AccordionSelectItem, { useCustomStyle } from './AccordionSelectItem';
import Checkbox from '../../Checkbox';
import MenuItem from '../../MenuItem';

import Icon from '../../Icon';
import { useMUITheme } from '../../styles/theme';

const Accordion = withStyles({
  root: {
    borderTop: 'none',
    boxShadow: 'none',
    minHeight: 0,
    '&$expanded': {
      minHeight: 0,
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles(theme => ({
  root: {
    position: 'relative',
    flexDirection: 'row-reverse',
    padding: 0,
    minHeight: 0,
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&$expanded': {
      margin: 0,
      minHeight: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.lightest,
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.lightest,
    },
  },
  content: {
    margin: 0,
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&$expanded': {
      margin: 0,
      minHeight: 0,
    },
  },
  expanded: {},
  expandIcon: {
    position: 'absolute',
    left: 0,
    marginLeft: '8px',
    padding: 0,
    color: theme.palette.grey[500],
    width: '16px',
    height: '16px',
  },
}))(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    padding: 0,
  },
})(MuiAccordionDetails);

export type OptionData = {
  text: string;
  value: string;
};

interface AccordionSelectGroupProps {
  mainOption: OptionData;
  subOptions: OptionData[];
  selectedValue: string[];
  handleChangeValue: (value: string[]) => void;
}

const isAllSubOptionsIncluded = (
  value: string[] = [],
  subOptions: OptionData[]
) => subOptions.every(subOption => value.includes(subOption.value));

const AccordionSelectGroup = forwardRef<
  HTMLDivElement,
  AccordionSelectGroupProps
>(({ mainOption, subOptions, handleChangeValue, selectedValue }, ref) => {
  const theme = useMUITheme();
  const customOptionStyle = useCustomStyle(theme);
  const isSelectAll = selectedValue.includes(mainOption.value);
  const optionValues = subOptions.map(option => option.value);
  const [expanded, setExpanded] = useState(false);

  const onChangeSubValue = (subValue: string) => {
    let newValue = selectedValue.slice();
    // 把因選取題目後摺疊的選項攤平
    if (isSelectAll) {
      newValue = newValue
        .filter(v => v !== mainOption.value)
        .concat(optionValues);
    }
    if (newValue.includes(subValue)) {
      // 移除
      newValue = newValue.filter(v => v !== subValue);
      handleChangeValue(newValue);
    } else {
      // 新增
      newValue.push(subValue);
      if (isAllSubOptionsIncluded(newValue, subOptions)) {
        newValue = newValue.filter(v => !optionValues.includes(v));
        newValue.push(mainOption.value);
      }
      handleChangeValue(newValue);
    }
  };

  const onChangeMainValue = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    let newValue = selectedValue.slice();
    if (isSelectAll) {
      handleChangeValue(newValue.filter(v => v !== mainOption.value));
    } else {
      newValue.push(mainOption.value);
      newValue = newValue.filter(v => !optionValues.includes(v));
      handleChangeValue(newValue);
    }
  };

  return (
    <Accordion ref={ref} expanded={expanded}>
      <AccordionSummary
        expandIcon={subOptions.length > 0 &&<Icon name="ExpandMore" />}
        className={cx({ selected: isSelectAll })}
        IconButtonProps={{
          // toggle accordion only through the expand icon
          onClick: e => {
            if (subOptions.length === 0) return;
            e.stopPropagation();
            setExpanded(!expanded);
            if (window) {
              setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
              }, 100);
            }
          },
        }}
        onClick={onChangeMainValue}
      >
        <MenuItem
          className={cx(
            customOptionStyle.container,
            {
              ['Mui-selected']: isSelectAll,
            },
            css`
              width: 100%;
              padding: 8px 8px 8px 24px;
            `
          )}
          value={mainOption.value}
          selected={isSelectAll}
        >
          <Checkbox
            color="primary"
            checked={isSelectAll}
            indeterminate={
              !isSelectAll && selectedValue.some(v => optionValues.includes(v))
            }
            onClick={onChangeMainValue}
            className={customOptionStyle.checkbox}
          />
          <div className={customOptionStyle.greyBlock}>{mainOption.text}</div>
        </MenuItem>
      </AccordionSummary>
      <AccordionDetails>
        {subOptions.map(option => (
          <div key={option.value}>
            <AccordionSelectItem
              value={option.value}
              checked={selectedValue.includes(option.value) || isSelectAll}
              text={option.text}
              handleChangeValue={onChangeSubValue}
            />
          </div>
        ))}
      </AccordionDetails>
    </Accordion>
  );
});

export default AccordionSelectGroup;
